// Every modal on the site (CLAUDE.md: Modals).
//
// Centred horizontally and vertically, one width, the close button always in the
// same place. The site header stays visible behind the overlay — dimmed, never
// hidden — which is the one place the build departs from the design file.
import { useEffect, useRef, type ReactNode } from 'react';
import './Modal.css';

interface Props {
  title: string;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  /** The window holds still and one part inside it scrolls (`.modal-scroll`),
   *  rather than the whole window scrolling — the archive's grid. */
  fixedHead?: boolean;
}

// One tab per game, in the Archive and the Stats windows alike.
export function ModalTabs<T extends string>(
  { tabs, selected, onSelect }: { tabs: { id: T; name: string }[]; selected: T; onSelect: (id: T) => void }
) {
  return (
    <div className="modal-tabs" role="tablist">
      {tabs.map((tab) => (
        <button
          type="button"
          role="tab"
          aria-selected={selected === tab.id}
          className={`modal-tab${selected === tab.id ? ' is-selected' : ''}`}
          key={tab.id}
          onClick={() => onSelect(tab.id)}
        >
          {tab.name}
        </button>
      ))}
    </div>
  );
}

export function Modal({ title, open, onClose, children, fixedHead = false }: Props) {
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    panel.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // The page behind holds still while a window is open. Two reasons: scrolling
  // over the dimmed area used to move the page underneath, which is nobody's
  // intention; and now that the header scrolls with the page rather than being
  // pinned, scrolling behind a modal would carry it off screen — and the rule is
  // that the header stays visible, dimmed, behind an open window.
  useEffect(() => {
    if (!open) return;
    const body = document.body;
    const y = window.scrollY;
    // Lifting the page out of the flow and holding it at its own offset, rather
    // than `overflow: hidden`: iOS Safari scrolls the page behind a hidden body
    // anyway, and iOS is the phone this is tested on. The padding replaces the
    // scrollbar's own width, or the page jumps sideways as it disappears.
    const gap = window.innerWidth - document.documentElement.clientWidth;
    const was = body.style.cssText;
    body.style.position = 'fixed';
    body.style.top = `${-y}px`;
    body.style.left = '0';
    body.style.right = '0';
    if (gap > 0) body.style.paddingRight = `${gap}px`;
    return () => {
      body.style.cssText = was;
      window.scrollTo(0, y);   // put them back exactly where they were reading
    };
  }, [open]);

  // **A fixed-head window never ends over the Share button** (owner, 25
  // October). Where the page behind has one on screen, the window reaches at
  // least 16px past its foot; `max-height` still caps it at the room the
  // overlay leaves. Measured on opening and on every resize, since Share sits
  // in a different place on each layout and each result screen.
  useEffect(() => {
    if (!open || !fixedHead) return;
    const fit = () => {
      const box = panel.current;
      if (!box) return;
      const share = document.querySelector<HTMLElement>('[data-share]')?.getBoundingClientRect();
      // offsetTop, not the box's rect: the window rises 8px as it opens, and
      // a rect read mid-rise would come out short by that much.
      const top = box.offsetTop;
      // Capped at the room the overlay leaves (a min-height beats max-height).
      // Where Share reaches into the gutter under that room, the window runs
      // to the foot of the screen instead, or a strip of Share shows there.
      const overlay = box.parentElement!;
      const room = overlay.clientHeight - parseFloat(getComputedStyle(overlay).paddingBottom) - top;
      const want = share && share.bottom > top && share.top < window.innerHeight
        ? Math.ceil(share.bottom + 16 - top) : 0;
      const full = window.innerHeight - top;
      const height = want > room ? full : want;
      box.style.minHeight = height ? `${height}px` : '';
      box.style.maxHeight = height > room ? `${full}px` : '';
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [open, fixedHead]);

  if (!open) return null;

  return (
    <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`modal${fixedHead ? ' modal-fixed-head' : ''}`} role="dialog" aria-modal="true" aria-label={title} ref={panel} tabIndex={-1}>
        <div className="modal-head">
          <h2 className="modal-title">{title}</h2>
          <button type="button" className="icon-button modal-close" aria-label="Close" onClick={onClose}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
